import { AccountType, CompanyType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';

import useAccount from '~hooks/useAccount/useAccount';
import { useDepartment } from '~hooks/useDepartment/useDepartment';
import { useDrivers } from '~hooks/useDrivers/useDrivers';
import { EquipmentTypeahead } from '~hooks/useEquipment/models';
import useOrdersDispatch from '~hooks/useOrders/useOrdersDispatch';
import useProjects from '~hooks/useProjects/useProjects';
import { Site } from '~hooks/useSites/models';
import {
  GetCompanySitesDispatchTypeaheadProps,
  useSites,
} from '~hooks/useSites/useSites';
import useUsers from '~hooks/useUsers/useUsers';
import { getConsolidatedEquipmentName } from '~utils/drivers/driver-utils';

import { FilterMenuOption } from './FilterMenuItem';

const QUERY_PAGINATION_LIMIT = 10;

export type FetchFunction = (
  query: string,
  cursor?: string,
) => Promise<{ data: FilterMenuOption[]; nextPageCursor: string | undefined }>;

export type FetchDriversFunction = (
  query: string,
  sharedDriversCursor?: string,
  nonSharedDriversCursor?: string,
) => Promise<{
  data: FilterMenuOption[];
  nextSharedDriversPageCursor: string | undefined;
  nextNonSharedDriversPageCursor: string | undefined;
}>;

export const getEquipmentLabel = (equipment: EquipmentTypeahead) => {
  if (!equipment) return '';
  return `${equipment?.companyShare?.externalId || equipment?.name} • ${equipment?.equipmentType?.name}`;
};

export default function useFetchers(userCompanyId?: string) {
  const [companyId, setCompanyId] = useState(userCompanyId);

  const { getAccountsByCompanyIdTypeahead } = useAccount();
  const { getDriversByCompanyIdTypeahead } = useDrivers();
  const { getProjectsByCompanyIdTypeahead, getProjectExternalIdsTypeahead } =
    useProjects();
  const { getCompanySitesTypeahead, getCompanySitesDispatchTypeahead } = useSites();
  const {
    getCompanyEquipmentsTypeahead,
    getJobEquipmentsTypeahead,
    getOrderDispatchNumbersByCompanyTypeahead,
  } = useOrdersDispatch();
  const { getCompanyUsersTypeahead } = useUsers();
  const { getDepartmentByCompanyIdTypeahead } = useDepartment();

  const fetchCustomers = useCallback<FetchFunction>(
    async (query, cursor?) => {
      if (!companyId) {
        throw new Error('Company ID not set');
      }

      try {
        const response = await getAccountsByCompanyIdTypeahead({
          accountTypes: [AccountType.CUSTOMER],
          companyId,
          cursor,
          query,
          searchParams: { limit: QUERY_PAGINATION_LIMIT },
        });

        if (!response) {
          throw new Error();
        }

        const data: FilterMenuOption[] = response.data.map((account) => ({
          label: account.name,
          value: account.id,
        }));

        return { data, nextPageCursor: response.pagination.after };
      } catch (error) {
        console.error(error);
        throw new Error('Customers request failed');
      }
    },
    [companyId],
  );

  const fetchOrdersDispatchNumbers = useCallback<FetchFunction>(
    async (query, cursor?) => {
      if (!companyId) {
        throw new Error('Company ID not set');
      }

      try {
        const response = await getOrderDispatchNumbersByCompanyTypeahead({
          query,
          limit: QUERY_PAGINATION_LIMIT,
          cursor,
          companyId,
        });

        if (!response) {
          throw new Error();
        }

        const data: FilterMenuOption[] = response.data.map((item) => ({
          label: item.dispatchNumber,
          value: item.id,
        }));

        return { data, nextPageCursor: response.pagination.after };
      } catch (error) {
        console.error(error);
        throw new Error('Orders dispatch numbers request failed');
      }
    },
    [companyId],
  );

  const fetchDrivers = useCallback<FetchDriversFunction>(
    async (query, sharedDriversCursor?, nonSharedDriversCursor?) => {
      if (!companyId) {
        throw new Error('Company ID not set');
      }

      try {
        const driversRequests = [true, false].map((shared) => {
          const cursor = shared ? sharedDriversCursor : nonSharedDriversCursor;

          return getDriversByCompanyIdTypeahead({
            companyId,
            shared,
            searchParams: {
              query,
              limit: Math.floor(QUERY_PAGINATION_LIMIT / 2),
              ...(cursor ? { link: { cursor, type: 'after' } } : undefined),
            },
          });
        });
        const driversResponses = await Promise.all(driversRequests);

        if (!driversResponses) {
          throw new Error();
        }

        const data: FilterMenuOption[] = driversResponses
          .flatMap((response) => response?.data || [])
          .map((driver) => {
            const equipmentName = getConsolidatedEquipmentName(driver);
            const label = equipmentName
              ? `${equipmentName}- ${driver.fullName}`
              : driver.fullName;

            return { label, value: driver.id };
          });

        return {
          data,
          nextSharedDriversPageCursor: driversResponses?.[0]?.pagination.after,
          nextNonSharedDriversPageCursor: driversResponses?.[1]?.pagination.after,
        };
      } catch (error) {
        console.error(error);
        throw new Error('Drivers request failed');
      }
    },
    [companyId],
  );

  const fetchEquipments = useCallback<FetchFunction>(
    async (query, cursor?) => {
      if (!companyId) {
        throw new Error('Company ID not set');
      }

      try {
        const response = await getCompanyEquipmentsTypeahead(companyId, query, {
          limit: QUERY_PAGINATION_LIMIT,
          after: cursor,
        });

        const data: FilterMenuOption[] = response.data.map((equipment) => ({
          label: getEquipmentLabel(equipment),
          value: equipment.id,
        }));

        return { data, nextPageCursor: response.pagination.after };
      } catch (error) {
        console.error(error);
        throw new Error('Equipments request failed');
      }
    },
    [companyId],
  );

  const fetchJobEquipments = useCallback(
    (jobId: string): FetchFunction =>
      async (query, cursor?) => {
        if (!companyId) {
          throw new Error('Company ID not set');
        }

        try {
          const response = await getJobEquipmentsTypeahead(companyId, jobId, query, {
            limit: QUERY_PAGINATION_LIMIT,
            after: cursor,
          });

          const data: FilterMenuOption[] = response.data.map((equipment) => ({
            label: getEquipmentLabel(equipment),
            value: equipment.id,
          }));

          return { data, nextPageCursor: response.pagination.after };
        } catch (error) {
          console.error(error);
          throw new Error('Equipments request failed');
        }
      },
    [companyId],
  );

  const fetchProjects = useCallback<FetchFunction>(
    async (query, cursor?) => {
      if (!companyId) {
        throw new Error('Company ID not set');
      }

      try {
        const response = await getProjectsByCompanyIdTypeahead({
          companyId,
          cursor,
          limit: QUERY_PAGINATION_LIMIT,
          query,
        });

        if (!response) {
          throw new Error();
        }

        const data: FilterMenuOption[] = response.data.map((project) => ({
          label: project.name,
          value: project.id,
        }));

        return { data, nextPageCursor: response.pagination?.after };
      } catch (error) {
        console.error(error);
        throw new Error('Projects request failed');
      }
    },
    [companyId],
  );

  const fetchProjectExternalIds = useCallback<FetchFunction>(
    async (query, cursor?) => {
      if (!companyId) {
        throw new Error('Company ID not set');
      }

      try {
        const response = await getProjectExternalIdsTypeahead({
          companyId,
          cursor,
          limit: QUERY_PAGINATION_LIMIT,
          query,
        });

        if (!response) {
          throw new Error();
        }

        const data: FilterMenuOption[] = response.data
          .map((project) => ({ label: project.externalId, value: project.externalId }))
          .filter((project): project is FilterMenuOption => {
            return Boolean(project.label && project.value);
          });

        return { data, nextPageCursor: response.pagination?.after };
      } catch (error) {
        console.error(error);
        throw new Error('External Projects IDs request failed');
      }
    },
    [companyId],
  );

  const fetchRequesters = useCallback<FetchFunction>(
    async (query, cursor?) => {
      if (!companyId) {
        throw new Error('Company ID not set');
      }

      try {
        const response = await getCompanyUsersTypeahead({
          companyId,
          cursor,
          query,
          limit: QUERY_PAGINATION_LIMIT,
        });

        if (!response) {
          throw new Error();
        }

        const data: FilterMenuOption[] = response.data.map((user) => ({
          label: user.fullName,
          value: user.id,
        }));

        return { data, nextPageCursor: response.pagination.after };
      } catch (error) {
        console.error(error);
        throw new Error('Requesters request failed');
      }
    },
    [companyId],
  );

  const fetchVendors = useCallback<FetchFunction>(
    async (query, cursor?) => {
      if (!companyId) {
        throw new Error('Company ID not set');
      }

      try {
        const response = await getAccountsByCompanyIdTypeahead({
          accountTypes: [AccountType.VENDOR],
          companyId,
          cursor,
          query,
          searchParams: { limit: QUERY_PAGINATION_LIMIT },
        });

        if (!response) {
          throw new Error();
        }

        const data: FilterMenuOption[] = response.data.map((account) => ({
          label: account.name,
          value: account.id,
        }));

        return { data, nextPageCursor: response.pagination.after };
      } catch (error) {
        console.error(error);
        throw new Error('Vendors request failed');
      }
    },
    [companyId],
  );

  const fetchSites = useCallback<FetchFunction>(
    async (query, cursor?) => {
      if (!companyId) {
        throw new Error('Company ID not set');
      }

      try {
        const response = await getCompanySitesTypeahead({
          companyId,
          cursor,
          query,
          limit: QUERY_PAGINATION_LIMIT,
        });

        if (!response) {
          throw new Error();
        }

        const data: FilterMenuOption[] = (response.data as Site[]).map((site) => ({
          label: site.name,
          value: site.id,
        }));

        return { data, nextPageCursor: response.pagination.after };
      } catch (error) {
        console.error(error);
        throw new Error('Sites request failed');
      }
    },
    [companyId],
  );

  const fetchDispatchSites = useCallback<
    (
      siteType: Exclude<GetCompanySitesDispatchTypeaheadProps['siteType'], undefined>,
    ) => FetchFunction
  >(
    (siteType) => {
      return async (query, cursor?) => {
        if (!companyId) {
          throw new Error('Company ID not set');
        }

        try {
          const response = await getCompanySitesDispatchTypeahead({
            companyId,
            cursor,
            query,
            limit: QUERY_PAGINATION_LIMIT,
            siteType,
          });

          if (!response) {
            throw new Error();
          }

          const data: FilterMenuOption[] = (response.data as unknown as Site[]).map(
            (site) => ({
              label: site.name,
              value: site.id,
            }),
          );

          return { data, nextPageCursor: response.pagination.after };
        } catch (error) {
          console.error(error);
          throw new Error('Sites request failed');
        }
      };
    },
    [companyId],
  );

  const fetchDepartments = useCallback<FetchFunction>(
    async (query, cursor?) => {
      if (!companyId) {
        throw new Error('Company ID not set');
      }

      try {
        const response = await getDepartmentByCompanyIdTypeahead({
          companyId,
          cursor,
          query,
          limit: QUERY_PAGINATION_LIMIT,
        });

        if (!response) {
          throw new Error();
        }

        const data: FilterMenuOption[] = response.data.map((department) => ({
          label: department.name,
          value: department.id,
        }));

        return { data, nextPageCursor: response.pagination.after };
      } catch (error) {
        console.error(error);
        throw new Error('Departments request failed');
      }
    },
    [companyId],
  );

  useEffect(() => {
    setCompanyId(userCompanyId);
  }, [userCompanyId]);

  const fetchCompanyTypes = useCallback<FetchFunction>((query) => {
    const companyTypes = Object.values(CompanyType).filter((companyType) => {
      return companyType.toLowerCase().includes(query.toLowerCase());
    });

    const data: FilterMenuOption[] = companyTypes.map((companyType) => ({
      label: t(`enums.company_type.${companyType}`),
      value: companyType,
    }));

    return Promise.resolve({ data, nextPageCursor: undefined });
  }, []);

  return {
    fetchCompanyTypes,
    fetchCustomers,
    fetchDepartments,
    fetchDispatchSites,
    fetchDrivers,
    fetchEquipments,
    fetchJobEquipments,
    fetchOrdersDispatchNumbers,
    fetchProjectExternalIds,
    fetchProjects,
    fetchRequesters,
    fetchSites,
    fetchVendors,
  };
}
